import React, {useMemo} from 'react';
import {
  ActivityIndicator,
  Dimensions,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import {BarChart, PieChart} from 'react-native-chart-kit';
import {Card} from 'react-native-paper';
import {NavigationProp, useNavigation} from '@react-navigation/native';
import {theme} from '../../theme/theme';
import {ReportChartEntry} from './registry';

import T from '../../Components/T';
const PALETTE = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#14b8a6', '#ec4899', '#6366f1'];
const chartWidth = Dimensions.get('window').width - 64;

const compact = (v: number) =>
  v >= 10000000
    ? `${(v / 10000000).toFixed(1)}Cr`
    : v >= 100000
    ? `${(v / 100000).toFixed(1)}L`
    : v >= 1000
    ? `${(v / 1000).toFixed(0)}K`
    : `${v}`;

interface Props {
  entry: ReportChartEntry;
  cid: string;
  fdt: string;
  tdt: string;
  /** Called after the Open link navigates (e.g. to close a modal). */
  onNavigate?: () => void;
}

const ReportChartCard = ({entry, cid, fdt, tdt, onNavigate}: Props) => {
  const navigation = useNavigation<NavigationProp<any>>();
  const {data, isFetching, isError} = entry.useData({cid, fdt, tdt, skip: !cid});
  const spec = useMemo(() => entry.toChart(data), [entry, data]);

  return (
    <Card style={styles.card} elevation={2}>
      <View style={styles.header}>
        <Text style={styles.title}>{entry.title}</Text>
        {entry.screen ? (
          <TouchableOpacity
            onPress={() => {
              navigation.navigate(entry.screen as string);
              onNavigate?.();
            }}>
            <Text style={styles.link}><T>Open</T></Text>
          </TouchableOpacity>
        ) : (
          <Text style={styles.webOnly}><T>Full report on web</T></Text>
        )}
      </View>

      {spec?.tiles && (
        <View style={styles.tiles}>
          {spec.tiles.map(t => (
            <View key={t.label} style={styles.tile}>
              <Text style={styles.tileLabel}>{t.label}</Text>
              <Text style={styles.tileValue}>{t.value}</Text>
            </View>
          ))}
        </View>
      )}

      {isFetching && !spec ? (
        <ActivityIndicator style={styles.pad} color={theme.colors.primary} />
      ) : isError ? (
        <Text style={[styles.empty, styles.error]}><T>Could not load this report</T></Text>
      ) : spec ? (
        spec.type === 'pie' ? (
          <PieChart
            data={spec.labels.map((l, i) => ({
              name: l,
              population: spec.values[i],
              color: PALETTE[i % PALETTE.length],
              legendFontColor: '#374151',
              legendFontSize: 11,
            }))}
            width={chartWidth}
            height={180}
            accessor="population"
            backgroundColor="transparent"
            paddingLeft="0"
            chartConfig={{color: () => '#000'}}
          />
        ) : (
          <BarChart
            data={{
              labels: spec.labels.map(l => l.substring(0, 6)),
              datasets: [{data: spec.values}],
            }}
            width={chartWidth}
            height={200}
            yAxisLabel="₹"
            yAxisSuffix=""
            fromZero
            chartConfig={{
              backgroundColor: theme.colors.white,
              backgroundGradientFrom: theme.colors.white,
              backgroundGradientTo: theme.colors.white,
              decimalPlaces: 0,
              formatYLabel: (v: string) => compact(Number(v)),
              color: () => theme.colors.primary,
              labelColor: () => '#6B7280',
            }}
          />
        )
      ) : (
        <Text style={styles.empty}><T>No data for the selected period</T></Text>
      )}
    </Card>
  );
};

const styles = StyleSheet.create({
  card: {marginBottom: 12, padding: 12, backgroundColor: theme.colors.white},
  header: {flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8},
  title: {fontSize: 15, fontWeight: '700', color: theme.colors.charcoal},
  link: {color: theme.colors.primary, fontWeight: '600'},
  webOnly: {color: theme.colors.mediumGray, fontSize: 11},
  tiles: {flexDirection: 'row', gap: 16, marginBottom: 4},
  tile: {},
  tileLabel: {fontSize: 11, color: theme.colors.darkGray},
  tileValue: {fontSize: 15, fontWeight: '700', color: theme.colors.charcoal},
  pad: {paddingVertical: 32},
  empty: {textAlign: 'center', color: theme.colors.darkGray, paddingVertical: 32},
  error: {color: '#dc2626'},
});

export default ReportChartCard;
